import React from "react";
import { createPortal } from "react-dom";
import Icon from "./components/Icon";
import { bytesLabel } from "./utils/formatters";
import { safeExternalUrl } from "./utils/fileUtils";

function getKind(item) {
  const mime = String(item?.mime || item?.type || "").toLowerCase();
  const kind = String(item?.kind || "").toLowerCase();
  const url = String(item?.url || "").toLowerCase();
  if (kind === "image" || mime.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(url)) return "image";
  if (kind === "video" || mime.startsWith("video/") || /\.(mp4|webm|mov|m4v)$/i.test(url)) return "video";
  if (kind === "audio" || mime.startsWith("audio/") || /\.(mp3|wav|ogg|m4a|aac)$/i.test(url)) return "audio";
  if (mime === "application/pdf" || /\.pdf$/i.test(url)) return "pdf";
  return "file";
}

function kindLabel(kind) {
  if (kind === "image") return "Изображение";
  if (kind === "video") return "Видео";
  if (kind === "audio") return "Аудио";
  if (kind === "pdf") return "PDF";
  return "Файл";
}

function renderPreview(current, safeUrl) {
  const kind = getKind(current);
  const name = current?.name || kindLabel(kind);

  if (kind === "image") {
    return <img className="media-gallery-img" src={safeUrl || ""} alt={name} />;
  }

  if (kind === "video" && safeUrl) {
    return <video className="media-gallery-video" src={safeUrl} controls preload="metadata" />;
  }

  if (kind === "audio" && safeUrl) {
    return (
      <div className="media-gallery-file-preview">
        <div className="media-gallery-file-icon">♪</div>
        <div className="media-gallery-file-title">{name}</div>
        <audio className="media-gallery-audio" src={safeUrl} controls preload="metadata" />
      </div>
    );
  }

  if (kind === "pdf" && safeUrl) {
    return (
      <iframe
        className="media-gallery-pdf"
        src={safeUrl}
        title={name}
        loading="lazy"
      />
    );
  }

  return (
    <div className="media-gallery-file-preview">
      <div className="media-gallery-file-icon">📎</div>
      <div className="media-gallery-file-title">{name}</div>
      <div className="media-gallery-file-sub">{bytesLabel(current?.size)}</div>
    </div>
  );
}

export default function ChatMediaGallery({
  images,
  index,
  onClose,
  onPrev,
  onNext,
  onSelect,
  onJumpToMessage,
}) {
  const items = Array.isArray(images) ? images : [];
  const count = items.length;

  const safeIndex = count > 0 ? Math.min(Math.max(Number(index) || 0, 0), count - 1) : 0;
  const current = items[safeIndex] || null;
  const safeUrl = safeExternalUrl(current?.url, { allowDataImage: true });
  const externalUrl = safeExternalUrl(current?.url);
  const currentKind = getKind(current);
  const hasPrev = safeIndex > 0;
  const hasNext = safeIndex < count - 1;
  const canJump = Boolean(current?.messageKey || current?.messageId || current?.clientId);

  React.useEffect(() => {
    const html = document.documentElement.style.overflow;
    const body = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = html;
      document.body.style.overflow = body;
    };
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
        return;
      }
      if (event.key === "ArrowLeft") {
        if (!hasPrev) return;
        event.preventDefault();
        onPrev?.();
        return;
      }
      if (event.key === "ArrowRight") {
        if (!hasNext) return;
        event.preventDefault();
        onNext?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [hasNext, hasPrev, onClose, onNext, onPrev]);

  if (!count) return null;

  const galleryNode = (
    <div className="media-gallery-overlay" onClick={onClose}>
      <div className="media-gallery-content" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="media-gallery-close" onClick={onClose} aria-label="Закрыть">
          <Icon size={20}>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </Icon>
        </button>

        <div className={`media-gallery-stage is-${currentKind}`}>{renderPreview(current, safeUrl)}</div>

        <div className="media-gallery-footer media-gallery-footer--stacked">
          <div className="media-gallery-footer-main">
            <span className="media-gallery-counter">{safeIndex + 1} / {count}</span>
            {current?.name ? <span className="media-gallery-name">{current.name}</span> : null}
            <span className="media-gallery-type">{kindLabel(currentKind)}</span>
          </div>

          <div className="media-gallery-actions">
            {canJump ? (
              <button
                type="button"
                onClick={() => onJumpToMessage?.(current.messageKey || current.messageId || current.clientId)}
              >
                Перейти к сообщению
              </button>
            ) : null}
            {externalUrl ? (
              <a href={externalUrl} target="_blank" rel="noreferrer noopener">Открыть</a>
            ) : null}
            {externalUrl ? (
              <a href={externalUrl} download={current?.name || undefined}>Скачать</a>
            ) : null}
          </div>
        </div>

        {count > 1 ? (
          <div className="media-gallery-thumbs" role="tablist" aria-label="Миниатюры медиа">
            {items.map((item, idx) => {
              const active = idx === safeIndex;
              const itemKind = getKind(item);
              const itemUrl = safeExternalUrl(item?.url, { allowDataImage: true });
              return (
                <button
                  key={String(item?.id || item?.url || item?.name || idx)}
                  type="button"
                  className={`media-gallery-thumb ${active ? "is-active" : ""}`}
                  role="tab"
                  aria-label={`Медиа ${idx + 1}`}
                  aria-selected={active}
                  onClick={() => onSelect?.(idx)}
                >
                  {itemKind === "image" && itemUrl ? (
                    <img src={itemUrl} alt={item?.name || `Медиа ${idx + 1}`} loading="lazy" />
                  ) : (
                    <span>{itemKind === "video" ? "▶" : itemKind === "audio" ? "♪" : itemKind === "pdf" ? "PDF" : "📎"}</span>
                  )}
                </button>
              );
            })}
          </div>
        ) : null}

        {count > 1 ? (
          <>
            <button type="button" className="media-gallery-nav media-gallery-nav--prev" disabled={!hasPrev} onClick={onPrev}>
              <Icon size={24}><polyline points="15 18 9 12 15 6" /></Icon>
            </button>
            <button type="button" className="media-gallery-nav media-gallery-nav--next" disabled={!hasNext} onClick={onNext}>
              <Icon size={24}><polyline points="9 18 15 12 9 6" /></Icon>
            </button>
          </>
        ) : null}
      </div>
    </div>
  );

  if (typeof document === "undefined") return galleryNode;
  return createPortal(galleryNode, document.body);
}
